import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'semantic-ui-react'
import { TooltipButton } from '@cbgms/components/form/TooltipButton'
import { IBodyCell, IColumn, PinnedStatus, SpecialIndex } from '@cbgms/components/grid/Types'
import { ActionComponentFn, ListRow } from './types'
import MenuIcon from '@mui/icons-material/Menu'
import SendIcon from '@mui/icons-material/Send'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import { makeStyles } from '@mui/styles'

interface UseColumnsFromFieldsProps<T = any> {
  actionColumnWidth?: number
  columns?: IColumn[]
  actions?: ActionComponentFn<T>
  startActions?: ActionComponentFn<T>
  onEdit?: (row: T) => void
  onMenu?: (event: any, row: T) => void
  onPendingLog?: (event: any, row: T) => void
  onPopupOpened?: (row: T) => void
  onPendingDelete?: (event: any, row: T) => void
  onView?: (row: T) => void
  onDelete?: (row: T, dryRun: boolean, reason?: string) => Promise<any>
  onShowDeleteModal: (row: T) => void
  refreshFunc?: () => Promise<void>
  startActionsHeader?: () => JSX.Element
  hideActions?: boolean
}

const useStyles = makeStyles({
  smallIcon: {
    fontSize: '14px',
    padding: '0px'
  }
})

export function useColumnsFromFields<T = any>(props: UseColumnsFromFieldsProps<T>): IColumn[] {
  const { t } = useTranslation()
  const classes = useStyles()
  const {
    actionColumnWidth = 120,
    columns = [],
    actions,
    startActions,
    onEdit,
    onMenu,
    onPendingLog,
    onPopupOpened,
    onPendingDelete,
    onView,
    onDelete,
    onShowDeleteModal,
    refreshFunc,
    startActionsHeader,
    hideActions
  } = props

  const actionButtonRenderer = useMemo(() => {
    return (row: ListRow<T>, cell: IBodyCell) => {
      return (
        <div style={{ textAlign: 'center' }}>
          {cell.visible && (
            <Button.Group size='mini'>
              {onView && <TooltipButton title={t('global.open')} icon='search' onClick={() => onView(row.data)} />}
              {onEdit && <TooltipButton title={t('global.edit')} icon='pencil' onClick={() => onEdit(row.data)} data-testid='editButton' />}
              {onMenu && (
                <TooltipButton
                  title={t('global.menu')}
                  icon={<MenuIcon className={classes.smallIcon} />}
                  onClick={(event: any) => onMenu(event, row.data)}
                  data-testid='menuButton'
                />
              )}

              {onPopupOpened ? (
                <TooltipButton
                  title={t('global.popup')}
                  icon={<SearchIcon className={classes.smallIcon} />}
                  onClick={() => onPopupOpened(row.data)}
                  data-testid='popupButton'
                />
              ) : null}

              {(onPendingLog || onPendingDelete) && (
                <>
                  {onPendingLog && (
                    <TooltipButton
                      title={t('global.send')}
                      onClick={event => onPendingLog(event, row.data)}
                      data-testid='sendButton'
                      icon={<SendIcon className={classes.smallIcon} />}
                    />
                  )}
                  {onPendingDelete && (
                    <TooltipButton
                      onClick={event => onPendingDelete(event, row.data)}
                      data-testid='pendingDeleteButton'
                      icon={<DeleteIcon className={classes.smallIcon} />}
                      title={t('global.delete')}
                    />
                  )}
                </>
              )}

              {onDelete && (
                <TooltipButton
                  type='button'
                  title={t('global.delete')}
                  icon='trash'
                  onClick={() => onShowDeleteModal(row.data)}
                  data-testid='deleteButton'
                />
              )}
              {actions?.(row, refreshFunc)}
            </Button.Group>
          )}
        </div>
      )
    }
  }, [actions, onView, onEdit, onDelete, onShowDeleteModal, refreshFunc])

  const endActionColumn: IColumn = useMemo(() => {
    return {
      key: 'end-actions',
      name: t('global.actions'),
      width: actionColumnWidth,
      pinned: PinnedStatus.Right,
      index: SpecialIndex.Last,
      visible: true,
      static: true,
      localOnly: true,
      action: actionButtonRenderer
    }
  }, [actionButtonRenderer])

  const startActionColumn: IColumn | null = useMemo(() => {
    if (!startActions) return null

    return {
      key: 'start-actions',
      name: '\u00a0', // non breaking space
      width: 80,
      filterComponent: startActionsHeader,
      pinned: PinnedStatus.Left,
      index: SpecialIndex.First,
      visible: true,
      static: true,
      localOnly: true,
      action: (row: ListRow<T>) => {
        return <div style={{ textAlign: 'center' }}>{startActions(row, refreshFunc)}</div>
      }
    }
  }, [startActions])
  if (hideActions) return columns
  const combinedColumns = [...columns, endActionColumn]

  if (startActionColumn) combinedColumns.unshift(startActionColumn)

  return combinedColumns
}
